import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { EnvironmentService } from '../environment.service';

export type BusinessProfileFeatureFlagResolution =
  'evaluated' | 'missing' | 'timeout';

export interface BusinessProfileFeatureFlagResponse {
  key: string;
  name: string;
  kind: 'boolean';
  _version: number;
  creationDate: number;
  value: boolean;
  featureFlagResolution: BusinessProfileFeatureFlagResolution;
}

export interface BusinessProfileSearchResultRecord {
  id: string | null;
  companyId?: string | null;
  businessName?: string | null;
  name: string | null;
  companyName: string | null;
  address: string | null;
  city: string | null;
  stateProvince: string | null;
  zipPostalCode: string | null;
}

export interface BusinessProfileSearchResponse {
  results: BusinessProfileSearchResultRecord[];
}

export interface BusinessProfileCompanyProfile {
  companyName: string | null;
  address: string | null;
  telephoneNumber?: string | null;
  yearsInBusiness?: number | null;
  legalForm?: string | null;
  stateProvince?: string | null;
  registrationStatus?: string | null;
  registrationId?: string | null;
  sic?: string | null;
  naicsCode?: string | null;
}

export interface BusinessProfileCompanyTradePayments {
  paydex: number | null;
  creditUtilization: number | null;
  highSpendUtilization: number | null;
}

export interface BusinessProfileCompanyRiskAssessment {
  delinquencyScore: number | null;
  failureScore: number | null;
  viabilityScore: number | null;
  viabilityDataDepth: string | null;
  overallBusinessRisk: string | null;
  originationLeaseScore?: number | null;
  originationCardScore?: number | null;
  originationLoanScore?: number | null;
  maximumCreditRecommendation?: number | null;
  wexAiRiskGrade?: number | null;
  wexAiScore?: number | null;
  viabilityPortfolioRank?: number | null;
  viabilityProfile?: string | null;
  rawFailureScore?: number | null;
  rawDelinquencyScore?: number | null;
}

export interface BusinessProfileCompanyLegalEvents {
  bankruptcy?: {
    found: boolean | null;
    count?: number | null;
  } | null;
  liens?: {
    found: boolean | null;
    openNumber?: number | null;
    openValue?: number | null;
  } | null;
  suitsFound?: boolean | null;
}

export interface BusinessProfileCompanyResponse {
  companyId: string;
  companyProfile?: BusinessProfileCompanyProfile | null;
  tradePayments?: BusinessProfileCompanyTradePayments | null;
  riskAssessment?: BusinessProfileCompanyRiskAssessment | null;
  legalEvents?: BusinessProfileCompanyLegalEvents | null;
  lexisNexisSbfeScore?: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class BusinessProfileApiService {
  private readonly httpClient = inject(HttpClient);
  private readonly environmentService = inject(EnvironmentService);

  getFeatureFlag(
    featureFlagId: string,
  ): Promise<BusinessProfileFeatureFlagResponse> {
    return firstValueFrom(
      this.httpClient.get<BusinessProfileFeatureFlagResponse>(
        this.environmentService.businessProfileFeatureFlagApiPath(
          featureFlagId,
        ),
      ),
    );
  }

  searchBusinessProfiles(
    query: string,
  ): Promise<BusinessProfileSearchResponse> {
    return firstValueFrom(
      this.httpClient.get<BusinessProfileSearchResponse>(
        this.environmentService.businessProfileSearchApiPath(),
        {
          params: { q: query },
        },
      ),
    );
  }

  getCompanyById(id: string): Promise<BusinessProfileCompanyResponse> {
    return firstValueFrom(
      this.httpClient.get<BusinessProfileCompanyResponse>(
        this.environmentService.businessProfileCompanyByIdApiPath(id),
      ),
    );
  }
}
